
import { GooglePlace, resolvePlace, searchPlaces } from "@/utils/googleMaps";
import { Component, Prop, Vue } from "vue-property-decorator";

@Component
export default class GoogleAddressAutocomplete extends Vue {
  /** Bound address string (v-model). */
  @Prop({ default: "" }) readonly value!: string;
  @Prop({ default: "Address" }) readonly label!: string;
  @Prop({ default: "" }) readonly placeholder!: string;
  @Prop({ default: false }) readonly disabled!: boolean;
  @Prop({ default: true }) readonly outlined!: boolean;
  @Prop({ default: () => [] }) readonly errorMessages!: string[] | string;
  /** Debounce for the text search, in ms (~1s per LMS-1236). */
  @Prop({ default: 400 }) readonly debounce!: number;

  suggestions: GooglePlace[] = [];
  loading = false;
  showList = false;
  searchError = "";
  private debounceTimer: number | null = null;
  private searchSequence = 0;

  onInput(val: string): void {
    // Keep v-model / vee-validate in sync immediately.
    this.$emit("input", val);
    this.showList = true;
    this.queueSearch(val);
  }

  queueSearch(query: string): void {
    if (this.debounceTimer !== null) {
      window.clearTimeout(this.debounceTimer);
    }
    if (!query || !query.trim()) {
      this.suggestions = [];
      this.loading = false;
      return;
    }
    this.loading = true;
    this.searchError = "";
    const sequence = ++this.searchSequence;
    this.debounceTimer = window.setTimeout(async () => {
      try {
        const suggestions = await searchPlaces(query);
        if (sequence === this.searchSequence) {
          this.suggestions = suggestions;
        }
      } catch (e) {
        this.suggestions = [];
        this.searchError =
          "Address suggestions are unavailable. Check the Google Places API configuration.";
        this.$emit("error", e);
      } finally {
        this.loading = false;
      }
    }, this.debounce);
  }

  async selectPlace(place: GooglePlace): Promise<void> {
    this.loading = true;
    try {
      const resolved = await resolvePlace(place);
      this.$emit("input", resolved.address);
      this.$emit("select", resolved);
      this.searchError = "";
      this.suggestions = [];
      this.showList = false;
    } catch (e) {
      this.searchError = "Unable to load that address. Please try again.";
      this.$emit("error", e);
    } finally {
      this.loading = false;
    }
  }

  onBlur(): void {
    // Delay so a suggestion click (mousedown) still registers.
    window.setTimeout(() => {
      this.showList = false;
    }, 150);
  }

  beforeDestroy(): void {
    if (this.debounceTimer !== null) {
      window.clearTimeout(this.debounceTimer);
    }
  }
}
